import { DateTime, Duration } from "luxon";

import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import type { CoverageStore } from "@/models/sync/eventLogSync";

/**
 * Wound back from the server time a walk started at when its cursor is written, so a row stamped inside an
 * uncommitted transaction while the walk ran is still inside the next window. The overlap re-upserts idempotently.
 */
export const SYNC_CURSOR_OVERLAP_MS = Duration.fromObject({ seconds: 30 }).toMillis();

/** Read/write side of the persistent cursor store; production passes through to {@link BootstrapEventsRegistry}. */
export type SyncCursorStore = {
  getEventDate: (event: string, workspaceId: string) => Promise<Date | undefined>;
  setEventDate: (event: string, workspaceId: string, date: Date) => Promise<void>;
};

/** The instant a cursor is written for a walk that started at `requestStart`. */
export function cursorFor(requestStart: Date): Date {
  return DateTime.fromJSDate(requestStart).minus({ milliseconds: SYNC_CURSOR_OVERLAP_MS }).toJSDate();
}

export const registryCursorStore: SyncCursorStore = {
  getEventDate: (event, workspaceId) => BootstrapEventsRegistry.getInstance().getLatestEventDate(event, workspaceId),
  setEventDate: async (event, workspaceId, date) => {
    await BootstrapEventsRegistry.getInstance().markBootstrap(event, undefined, workspaceId, date);
  },
};

/** Read/write side of a persistent string state per key and workspace, such as an opaque server cursor. */
export type SyncStateStore = {
  /** The held state, or `undefined` when none is held or the store cannot be read. */
  get: (key: string, workspaceId: string) => Promise<string | undefined>;
  /** Holds `state`; the empty string clears it. */
  set: (key: string, workspaceId: string, state: string) => Promise<void>;
};

/**
 * String state kept in the registry's event state.
 *
 * The registry has no per-event delete, so a cleared state is written as the empty string and read back as none. Only
 * the state field is read: a date written under the same key through {@link registryCursorStore} is never taken for
 * one.
 */
export const registryStateStore: SyncStateStore = {
  get: async (key, workspaceId) => {
    const state = await BootstrapEventsRegistry.getInstance().getEventState(key, workspaceId);
    return state === "" ? undefined : state;
  },
  set: async (key, workspaceId, state) => {
    await BootstrapEventsRegistry.getInstance().setEventState(key, state, workspaceId);
  },
};

/** Registry key of a log's coverage: the spans of its `createdAt` axis the store holds. */
export function coverageKey(log: string, name: string): string {
  return `${log}:${name}:coverage`;
}

/**
 * Coverage persisted as JSON in the registry's event state, so it rides in the snapshot's app-meta with the rows.
 *
 * Reads are lenient on purpose: whatever parses comes back untyped for `normalizeCoverage` to read, which drops what
 * it cannot, so a corrupt entry cold boots the log rather than failing it.
 */
export const registryCoverageStore: CoverageStore = {
  get: async (key, workspaceId) => {
    const state = await BootstrapEventsRegistry.getInstance().getEventState(key, workspaceId);
    if (state === undefined) return undefined;
    try {
      return JSON.parse(state) as unknown;
    } catch {
      return undefined;
    }
  },
  set: async (key, workspaceId, coverage) => {
    await BootstrapEventsRegistry.getInstance().setEventState(key, JSON.stringify(coverage), workspaceId);
  },
  prune: async (prefix, workspaceId, keep) => {
    await BootstrapEventsRegistry.getInstance().pruneEventStates(prefix, keep, workspaceId);
  },
};

/**
 * `coverage` seeded from a log's legacy single cursor when it holds nothing under its key yet.
 *
 * The old cursor meant "every row in the window up to this instant is held", so it reads as one span from the window's
 * floor to the cursor; `normalizeCoverage` clamps or discards it as the floor has moved. The legacy key is left alone.
 *
 * @param legacyKeys - Each coverage key's legacy cursor key; a key not listed has no cursor to seed from.
 */
export function coverageStoreMigratingFrom(
  coverage: CoverageStore,
  cursors: SyncCursorStore,
  legacyKeys: ReadonlyMap<string, string>,
  depth: { hours: number },
  now: () => Date,
): CoverageStore {
  return {
    get: async (key, workspaceId) => {
      const held = await coverage.get(key, workspaceId);
      if (held !== undefined) return held;
      const legacyKey = legacyKeys.get(key);
      const cursor = legacyKey === undefined ? undefined : await cursors.getEventDate(legacyKey, workspaceId);
      if (cursor === undefined) return undefined;
      const floor = DateTime.fromJSDate(now()).minus({ hours: depth.hours }).toJSDate();
      return { spans: [{ from: floor.toISOString(), to: cursor.toISOString() }] };
    },
    set: coverage.set,
  };
}
